import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useHighwayPassesApi } from "../../http/highwayPasses/api";
import { HighwayPassNoCarDependency } from "../../http/highwayPasses/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { formatDateNumeric, isWithinLast48h } from "../../types/utils";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Table from "../ui/Table";

import { useUserSettings } from "../../context/userSettingsContext";
import SvgAdd from "../icons/Add";
import Button from "../ui/Button";
import CustomLink from "../ui/CustomLink";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CreateHighwayPassDialod from "./CreateHighwayPassDialog";
import DeleteHighwayPassDialog from "./DeleteHighwayPassDialog";
import EditHighwayPassDialog from "./EditHighwayPassButton";
import HighwayPassHistoryDialog from "./HighwayPassHistoryDialog";

const HIGHWAY_PASS_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.currentPlateNumber",
  "car.lastPlateNumber",
  "car.carSubsidiary.id",
  "car.carSubsidiary.subsidiary.id",
  "car.carSubsidiary.subsidiary.name",
  "car.carSubsidiary.startDate",
  "car.carSubsidiary.endDate",
  "startDate",
  "endDate",
  "nr",
  "versionNumber",
] as const;
type HighwayPassField = (typeof HIGHWAY_PASS_FIELDS)[number];
type HighwayPassSearch = NestedPick<HighwayPassNoCarDependency, HighwayPassField>;

type HighwayPassesProps = {
  onUpdate?: () => void;
};

const HighwayPasses: FC<HighwayPassesProps> = ({ onUpdate }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const highwayPassesApi = useHighwayPassesApi();
  const filters = useListFilters({ isSearchable: true, prefix: "hp" });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [showNew, setShowNew] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<HighwayPassSearch>();
  const [itemToEdit, setItemToEdit] = useState<HighwayPassSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<HighwayPassSearch>();
  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<HighwayPassNoCarDependency> = {
    ...filters.state,
    q: debouncedValue,
    fields: HIGHWAY_PASS_FIELDS,
  };

  const result = highwayPassesApi.useAdminHighwayPasses(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: highwayPassesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: HighwayPassSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.highwayPasses.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.highwayPasses.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<HighwayPassSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.highwayPasses.insert && (
            <Button buttonProps={{ onClick: () => setShowNew(true) }} glyph={SvgAdd} />
          ),
        }}
        head={
          <>
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.plateNumbers.singular}
                  fieldName="highwayPass.car.plateNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="highwayPass.car.vin"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}
            <Table.Th title={i18n.t.common.number} fieldName="highwayPass.number" order={orderOptions} />
            <Table.Th title={i18n.t.common.startDate} fieldName="highwayPass.startDate" order={orderOptions} />
            <Table.Th title={i18n.t.common.endDate} fieldName="highwayPass.endDate" order={orderOptions} />
            <Table.Th
              title={i18n.t.common.currentSubsidiary}
              fieldName="highwayPass.subsidiary.name"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            {!carId && (
              <>
                <td>
                  {isWithinLast48h(item.createdAt) && <NewItemIcon />}
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>
                      {item.car.currentPlateNumber || item.car.lastPlateNumber}
                    </CustomLink>
                  ) : (
                    <>{item.car.currentPlateNumber || item.car.lastPlateNumber}</>
                  )}
                </td>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
              </>
            )}
            <td>
              {!!carId && isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {item.nr}
            </td>
            <td>{!!item.startDate && formatDateNumeric(item.startDate, i18n.language)}</td>
            <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
            <td>
              {item.car.carSubsidiary && (
                <CustomLink to={`/admin/subsidiaries/${item.car.carSubsidiary.subsidiary.id}`}>
                  {item.car.carSubsidiary.subsidiary.name}
                </CustomLink>
              )}
            </td>
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />
      {!!itemToDelete && (
        <DeleteHighwayPassDialog
          highwayPassId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!itemToEdit && (
        <EditHighwayPassDialog
          highwayPassId={itemToEdit.id}
          onClose={(success) => {
            setItemToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!showNew && (
        <CreateHighwayPassDialod
          onClose={(success) => {
            setShowNew(false);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!itemToShowVersions && (
        <HighwayPassHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.highwayPasses.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default HighwayPasses;
